import { supabase } from '@/integrations/supabase/client';
import { getDeviceId } from '@/lib/deviceIdentity';
import { logger } from '@/lib/logger';
import type { DeviceRow, DeviceTrustResult } from '../types';

/**
 * Evaluate whether the current device is trusted for a given user.
 * Handles retry logic and auto-trust for sole-device scenarios.
 */
export async function evaluateDeviceTrust(userId: string): Promise<DeviceTrustResult> {
  const deviceId = getDeviceId();

  // First attempt
  let deviceRow = await fetchDeviceRow(userId, deviceId);

  // Retry once if not found (registration may still be settling)
  if (!deviceRow) {
    logger.warn('Device not found on first check, retrying after 1s...');
    await new Promise(r => setTimeout(r, 1000));
    deviceRow = await fetchDeviceRow(userId, deviceId);
  }

  if (!deviceRow) {
    logger.warn('Device not found after retry, allowing login temporarily');
    return { trusted: false, missing: true };
  }

  if (deviceRow.is_trusted) {
    return { trusted: true };
  }

  // Check if user has ANY other trusted device
  const hasOtherTrusted = await checkOtherTrustedDevices(userId, deviceId);

  if (!hasOtherTrusted) {
    // Auto-trust: no other trusted devices exist
    await autoTrustDevice(userId, deviceId);
    logger.debug('Auto-trusted device: no other trusted devices exist');
    return { trusted: true, autoTrusted: true };
  }

  return { trusted: false };
}

/**
 * Refresh device trust status (simple check, no auto-trust).
 */
export async function refreshDeviceTrustStatus(userId: string): Promise<boolean> {
  const deviceId = getDeviceId();
  const row = await fetchDeviceRow(userId, deviceId);
  return row?.is_trusted ?? false;
}

async function fetchDeviceRow(userId: string, deviceId: string): Promise<DeviceRow | null> {
  const { data } = await (supabase as any)
    .from('user_devices')
    .select('id, is_trusted')
    .eq('user_id', userId)
    .eq('device_id', deviceId)
    .maybeSingle();
  return (data as DeviceRow | null);
}

async function checkOtherTrustedDevices(userId: string, currentDeviceId: string): Promise<boolean> {
  const { data } = await (supabase as any)
    .from('user_devices')
    .select('id')
    .eq('user_id', userId)
    .eq('is_trusted', true)
    .neq('device_id', currentDeviceId)
    .limit(1);
  return !!data && (data as DeviceRow[]).length > 0;
}

async function autoTrustDevice(userId: string, deviceId: string): Promise<void> {
  await (supabase as any)
    .from('user_devices')
    .update({ is_trusted: true })
    .eq('user_id', userId)
    .eq('device_id', deviceId);
}
